/**
 * Report Content Edit Area - Isolated editor state for production performance
 *
 * Owns editedContent state so typing does not re-render the entire ReportViewer.
 * For very long documents (e.g. 77k words), paginates so only the current page
 * is loaded into the editor; adjacent pages are not rendered until the user navigates.
 * Exposes getContent/setContent via ref for Save/Cancel in parent.
 */

import React, { useState, useEffect, useImperativeHandle, useRef, forwardRef, useCallback, Suspense } from 'react';
import type { RichTextEditorRef } from './RichTextEditor';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { lazyWithRetry } from '../../utils/lazyWithRetry';

const RichTextEditor = lazyWithRetry(() =>
  import('./RichTextEditor').then((m) => ({ default: m.RichTextEditor }))
);

const WORDS_PER_PAGE = 2500;

/** Split markdown into pages by word count, breaking at line boundaries so we never cut mid-line. */
function splitMarkdownIntoPages(markdown: string, wordsPerPage: number = WORDS_PER_PAGE): string[] {
  const trimmed = (markdown ?? '').trim();
  if (!trimmed) return [''];
  const lines = markdown!.split('\n');
  const pages: string[] = [];
  let currentPage: string[] = [];
  let currentWords = 0;
  for (const line of lines) {
    const lineWords = line.trim().split(/\s+/).filter((w) => w.length > 0).length;
    if (currentWords + lineWords > wordsPerPage && currentPage.length > 0) {
      pages.push(currentPage.join('\n'));
      currentPage = [];
      currentWords = 0;
    }
    currentPage.push(line);
    currentWords += lineWords;
  }
  if (currentPage.length > 0) pages.push(currentPage.join('\n'));
  return pages.length ? pages : [''];
}

export interface ReportTemplateResponse {
  sections?: { name: string; description?: string }[];
  default_questions?: { section: string; question: string }[];
  template_source?: string;
}

export interface ReportContentEditAreaProps {
  initialContent: string;
  isEditing: boolean;
  viewMode: 'markdown' | 'rendered';
  reportTemplateData?: ReportTemplateResponse | null;
  selectedVersion: string | null;
  convertMarkdownToHTML: (markdown: string) => string;
  preprocessMarkdownWithFullRequirements: (markdown: string, template: ReportTemplateResponse | undefined) => string;
  /** When user clicks a citation pill [N] in rendered content, jump to Citations & Evidence Trail */
  onCitationClick?: (citationIndex: number) => void;
}

export interface ReportContentEditAreaRef {
  getContent: () => string;
  setContent: (content: string) => void;
}

export const ReportContentEditArea = forwardRef<ReportContentEditAreaRef, ReportContentEditAreaProps>(
  function ReportContentEditArea(
    {
      initialContent,
      isEditing,
      viewMode,
      reportTemplateData,
      selectedVersion,
      convertMarkdownToHTML,
      preprocessMarkdownWithFullRequirements,
      onCitationClick,
    },
    ref
  ) {
    const [pages, setPages] = useState<string[]>(() =>
      splitMarkdownIntoPages(initialContent ?? '')
    );
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const richEditorRef = useRef<RichTextEditorRef | null>(null);
    const pagesRef = useRef<string[]>(pages);
    const currentPageIndexRef = useRef(currentPageIndex);
    pagesRef.current = pages;
    currentPageIndexRef.current = currentPageIndex;

    useEffect(() => {
      const next = initialContent ?? '';
      setPages(splitMarkdownIntoPages(next));
      setCurrentPageIndex(0);
    }, [initialContent]);

    useImperativeHandle(
      ref,
      () => ({
        getContent: () => {
          const p = pagesRef.current;
          const i = currentPageIndexRef.current;
          const pageContent = richEditorRef.current?.getContent() ?? p[i] ?? '';
          const merged = [...p];
          merged[i] = pageContent;
          return merged.join('\n');
        },
        setContent: (content: string) => {
          setPages(splitMarkdownIntoPages(content));
          setCurrentPageIndex(0);
        },
      }),
      []
    );

    const handleChange = useCallback((markdown: string) => {
      setPages((prev) => {
        const next = [...prev];
        next[currentPageIndexRef.current] = markdown;
        return next;
      });
    }, []);

    const goToPage = useCallback((newIndex: number) => {
      const currentContent = richEditorRef.current?.getContent();
      if (currentContent !== undefined) {
        setPages((prev) => {
          const next = [...prev];
          next[currentPageIndexRef.current] = currentContent;
          return next;
        });
      }
      setCurrentPageIndex(newIndex);
    }, []);

    const currentPageContent = pages[currentPageIndex] ?? '';

    if (isEditing) {
      const totalPages = pages.length;
      const showPagination = totalPages > 1;
      const paginationBar = showPagination ? (
        <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Page {currentPageIndex + 1} of {totalPages}
            <span className="ml-2 text-gray-500 font-normal">
              (~{WORDS_PER_PAGE.toLocaleString()} words per page)
            </span>
          </span>
          <div className="flex items-center gap-2">
            <label className="sr-only" htmlFor="report-page-select">
              Go to page
            </label>
            <select
              id="report-page-select"
              value={currentPageIndex + 1}
              onChange={(e) => goToPage(parseInt(e.target.value, 10) - 1)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: totalPages }, (_, i) => (
                <option key={i} value={i + 1}>
                  Page {i + 1}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => goToPage(currentPageIndex - 1)}
              disabled={currentPageIndex <= 0}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            <button
              type="button"
              onClick={() => goToPage(currentPageIndex + 1)}
              disabled={currentPageIndex >= totalPages - 1}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null;
      return (
        <div className="space-y-2">
          {paginationBar}
          <Suspense
            fallback={
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-theme-paper min-h-[600px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <span className="ml-2 text-sm text-gray-600">Loading editor…</span>
              </div>
            }
          >
            <RichTextEditor
              ref={richEditorRef}
              value={currentPageContent}
              onChange={handleChange}
              placeholder="Enter report content in Markdown..."
              minHeight="600px"
            />
          </Suspense>
          {paginationBar}
        </div>
      );
    }

    // Read-only view
    const contentToShow = initialContent ?? '';
    if (!contentToShow) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading {selectedVersion || 'v2'} content...</p>
        </div>
      );
    }

    // v2 and v3+ rendered view: apply template preprocessing when available
    let contentForRender =
      viewMode === 'rendered' && reportTemplateData
        ? preprocessMarkdownWithFullRequirements(contentToShow, reportTemplateData)
        : contentToShow;

    // Strip Citations appendix and Evidence Gaps from rendered view — they are
    // parsed programmatically for the Evidence Trail tab and should not appear
    // as raw HTML anchor tags in the rendered content display.
    if (viewMode === 'rendered') {
      contentForRender = contentForRender.replace(/\n{0,2}---\n\n## Citations\b[\s\S]*$/, '');
      contentForRender = contentForRender.replace(/\n{0,2}---\n\n## Evidence Gaps[^\n]*\n[\s\S]*$/, '');
    }

    return (
      <div className="min-h-[600px]">
        {viewMode === 'rendered' ? (
          <div
            className="prose prose-slate max-w-none"
            dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(contentForRender) }}
            onClick={(e) => {
              const link = (e.target as HTMLElement).closest('.report-citation-link');
              if (link) {
                e.preventDefault();
                const idx = link.getAttribute('data-citation-index');
                if (idx != null) onCitationClick?.(parseInt(idx, 10));
              }
            }}
            onKeyDown={(e) => {
              const link = (e.target as HTMLElement).closest('.report-citation-link');
              if (link && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                const idx = link.getAttribute('data-citation-index');
                if (idx != null) onCitationClick?.(parseInt(idx, 10));
              }
            }}
          />
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
              {contentToShow}
            </pre>
          </div>
        )}
      </div>
    );
  }
);
